// camera.js
// OPTIMIZED VERSION - Direct video display, no canvas copying
import { detectExercise } from '../Exercise/ExerciseDetection.js';
import { getQualityProfile } from '../config/QualityProfiles.js';

let video, stream, animationFrameId;

// KNOX DATA CLEAR DETECTION: localStorage marker to detect external data clearing
const INIT_MARKER_KEY = 'mediapipe_init_marker';

// Dynamic resolution based on quality profile
let WIDTH, HEIGHT;
let poseFrameCount = 0;

export async function init(module, XR = false, containerId = "camera_canvas") {
    const gameModule = module;

    // ========== KNOX DATA CLEAR DETECTION ==========
    let initMarker = null;
    try {
        initMarker = localStorage.getItem(INIT_MARKER_KEY);
    } catch(e) {}

    const hasInstances = !!stream;

    if (!initMarker && hasInstances) {
        // CRITICAL: Data cleared externally but instances exist = KNOX CLEARED DATA
        if (stream) {
            stream.getTracks().forEach(t => {
                try { t.stop(); } catch(e) {}
            });
        }
        stream = null;
        video = null;
        animationFrameId = null;
    }

    try {
        // OPTIMIZATION: Get quality profile for adaptive settings
        const quality = getQualityProfile?.() || {
            camera: { width: 1280, height: 720, frameRate: 30 },
            pose: { processEveryFrame: 1, delegate: 'GPU' }
        };

        WIDTH = quality.camera.width;
        HEIGHT = quality.camera.height;
        poseFrameCount = 0;

        // Create video element for direct display (no canvas copying!)
        video = document.createElement('video');
        video.autoplay = true;
        video.muted = true;
        video.playsInline = true;
        
        // Style video for direct display - hardware accelerated
        // z-index: -1 keeps video BEHIND the game canvas and UI
        video.style.cssText = `
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            object-fit: cover;
            transform: scaleX(-1);
            z-index: -1;
            pointer-events: none;
        `;

        // Insert video as first child of body - ensures it's behind everything
        // Don't hide any existing canvas - game canvas needs to stay visible
        document.body.insertBefore(video, document.body.firstChild);

        stream = await navigator.mediaDevices.getUserMedia({
            video: {
                width: { ideal: WIDTH },
                height: { ideal: HEIGHT },
                frameRate: { ideal: quality.camera.frameRate, max: quality.camera.frameRate }
            }
        });

        video.srcObject = stream;
        
        await new Promise((resolve, reject) => {
            video.onloadedmetadata = () => {
                if (video.videoWidth > 0 && video.videoHeight > 0) {
                    resolve();
                } else {
                    reject(new Error("Video dimensions are invalid"));
                }
            };
            video.onerror = reject;
            setTimeout(() => reject(new Error("Video loading timeout")), 10000);
        });

        await video.play();

        if (!XR) {
            // Use requestVideoFrameCallback if available (more efficient)
            // Falls back to requestAnimationFrame
            const useVideoFrameCallback = 'requestVideoFrameCallback' in video;
            
            function processFrame() {
                if (!video || video.videoWidth === 0 || video.videoHeight === 0 ||
                    video.readyState < video.HAVE_ENOUGH_DATA) {
                    scheduleNextFrame();
                    return;
                }

                const timestamp = performance.now();

                // Frame skipping for pose detection
                poseFrameCount++;
                const shouldProcessPose = (poseFrameCount % quality.pose.processEveryFrame) === 0;

                if (shouldProcessPose) {
                    // Run ML on video element directly - no canvas copy needed!
                    detectExercise(video, timestamp).then(exerciseResult => {
                        if (exerciseResult?.landmarks && gameModule.updatePoseLandmarks) {
                            gameModule.updatePoseLandmarks(exerciseResult.landmarks);
                        }
                    }).catch(() => {
                        // Silent fail
                    });
                }

                scheduleNextFrame();
            }

            function scheduleNextFrame() {
                if (useVideoFrameCallback) {
                    video.requestVideoFrameCallback(processFrame);
                } else {
                    animationFrameId = requestAnimationFrame(processFrame);
                }
            }

            // Start the frame loop
            scheduleNextFrame();
        }

        // Set init marker
        try {
            localStorage.setItem(INIT_MARKER_KEY, `${Date.now()}_${Math.random()}`);
        } catch(e) {}
    } catch (error) {
        throw error;
    }
}

// Cleanly stop the video stream and remove video element
export async function close_stream() {
    if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
        animationFrameId = null;
    }

    // Stop and clean up the video stream
    if (stream) {
        stream.getTracks().forEach((track) => {
            track.stop();
        });
        stream = null;
    }

    // Remove video element from DOM
    if (video) {
        video.pause();
        video.srcObject = null;
        video.src = '';
        if (video.parentElement) {
            video.parentElement.removeChild(video);
        }
        video = null;
    }

    const isWebView = /wv|WebView/i.test(navigator.userAgent);
    if (isWebView) {
        await new Promise(resolve => setTimeout(resolve, 200));
    }

    try {
        localStorage.removeItem(INIT_MARKER_KEY);
    } catch(e) {}
}